import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useContext, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AuthContext } from "../../contexts/AuthContext";
import { login } from "../../services/authService";
import {
  themedPaperStyle,
  themedPrimaryButtonStyle,
} from "../../styles/themeStyles";

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const LoginForm = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useContext(AuthContext);
  const [error, setError] = useState("");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await login(values.email, values.password);

      // Store user and token in context
      authLogin(response.user, response.token);

      // Redirect based on role
      if (response.user.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Handle error response from the server
      if (error && typeof error === "object") {
        setError(error.message || "Login failed. Please try again.");
      } else {
        setError("Login failed. Please try again.");
      }
      console.error("Login error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={3}
        sx={{
          ...themedPaperStyle(),
          mt: { xs: 4, sm: 8 },
          p: { xs: 3, sm: 4 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.125rem" },
            mb: { xs: 2, sm: 3 },
          }}
        >
          Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <Box mb={{ xs: 2, sm: 3 }}>
                <Field
                  as={TextField}
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Box>

              <Box mb={{ xs: 2, sm: 3 }}>
                <Field
                  as={TextField}
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  size={window.innerWidth < 600 ? "small" : "medium"}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                size={window.innerWidth < 600 ? "medium" : "large"}
                sx={{
                  ...themedPrimaryButtonStyle(),
                  mb: 2,
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                {isSubmitting ? "Logging in..." : "Login"}
              </Button>

              <Box mt={2} textAlign="center">
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Forgot password?
                </Link>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default LoginForm;
